import Cropper from 'cropperjs';
import {
  SUBMISSION_RATIO, SUBMISSION_IMAGE_TYPE, SUBMISSION_HEIGHT, SUBMISSION_WIDTH
} from './../core/config';
import { devLog } from '../core/utils';
import { COLOR_BACKGROUND_SUBMISSION_FILL_BASE } from '../../design-tokens/build/dictionary';

/**
 * Options for Cropper#getCroppedCanvas
 */
const EXPORT_OPTIONS = {
  height: SUBMISSION_HEIGHT * 2,
  width: SUBMISSION_WIDTH * 2,
  fillColor: COLOR_BACKGROUND_SUBMISSION_FILL_BASE,
};

/**
 * Mixin for cropper.js bindings
 */
export default {
  name: 'cropper',
  props: {
    src: {
      type: String, // the src of the image that we will be cropping
    }
  },
  data() {
    return {
      initialized: false, // if cropperjs instance has been initalized
      cropper: null, // reference to cropperjs instance
      ready: false, // if the image is ready to crop
      initializeAttempt: 0, // how many times we've tried to initialize this to avoid infinite loops
      moving: false, // if the user is moving the image, used to make the overlay transparent so you get that "moving the image outside the preiview" effect
      resetting: false, // you have to destroy and recreate cropper after the image src changes. it's an async process so this makes sure we don't do it too often.
    };
  },
  mounted() {
    // attempt to init as soon as we mount. Will probably remove this init call once we add it to to the entry page. Default image will only be for videos without thumbnails.
    this.init();
    this.$emit('mounted');
  },
  beforeDestroy() {
    // destroy cropper instance
    if (this.cropper) {
      this.cropper.destroy();
    }
  },
  watch: {
    /**
     * Watch src to know when to reset the cropper instance
     *
     * @param {String} value
     * @param {String} oldValue
     * @returns {Null}
     */
    async src(value, oldValue) {
      if (value === oldValue) {
        return;
      }

      if (this.resetting) {
        return;
      }
      this.resetting = true;
      await this.$nextTick();

      this.reset();
    },
  },
  methods: {
    /**
     * When the image src changes, you have to tear down and create a new cropper instance
     *
     */
    reset() {
      this.log('reset');

      this.ready = false;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      this.initialized = false;
      this.init(this.src);
      this.resetting = false;
    },

    /**
     * Initialize cropper
     *
     * @returns {Promise.<null>} resolves when cropper is ready
     */
    init() {
      if (this.initialized) {
        this.log('Why are you re-initializing?');
        return null;
      }
      this.ready = false;
      this.$emit('ready', false);
      if (!this.$refs.image) {
        this.initializeAttempt += 1;
        if (this.initializeAttempt < 3) {
          this.log('Retry Initialize');
          return this.$nextTick().then(this.init());
        }
        throw new Error("Couldn't make Cropper");
      }

      const promise = new Promise((resolve) => {
        this.cropper = new Cropper(this.$refs.image, {
          viewMode: 3,
          aspectRatio: SUBMISSION_RATIO,
          modal: true,
          highlight: false,
          background: false,
          dragMode: 'move',
          movable: true,
          guides: true,
          cropBoxMovable: true,
          cropBoxResizable: true,
          autoCrop: true,
          autoCropArea: 1,
          toggleDragModeOnDblclick: false,
          // checkOrientation: false,
          ready: () => {
            this.ready = true;
            this.log('ready');

            console.log(
              this.cropper.getImageData()
            );
            const {
              rotate,
              naturalHeight,
              naturalWidth,
            } = this.cropper.getImageData();

            this.$emit('ready', {
              rotate,
              naturalHeight,
              naturalWidth,
            });

            resolve();
          },
          cropstart: (event) => {
            this.moving = true;
            this.log('moving', event);
          },
          cropend: () => {
            this.moving = false;
            this.log('not moving');
          },
          zoom: this.handleZoom
        });
      });

      this.initialized = true;
      this.log('initialized');

      return promise;
    },

    /**
     * zoom event handler. Just a log for now, will expand if we need to show the whole image while zooming. Zoom event doesn't have a start and end, hence why `this.moving` isn't working
     *
     */
    handleZoom() {
      this.log('zooming');
    },

    /**
     * Export the cropped canvas as an image url.
     *
     * @returns {Promise.<String>} imageData url
     */
    exportImage() {
      if (!this.cropper) {
        this.log('No cropper, no export.');
        return Promise.reject();
      }

      const canvas = this.cropper.getCroppedCanvas(EXPORT_OPTIONS);

      const data = canvas.toDataURL(SUBMISSION_IMAGE_TYPE);

      this.$emit('export', data);

      return Promise.resolve(data);
    },

    /**
     * Export the cropped canvas as an image blob for appending to form data
     *
     * @returns {Promise.<Blob>} canvas as a blob
     */
    exportBlob() {
      return new Promise((resolve, reject) => {
        if (!this.cropper) {
          this.log('No cropper, no export.');
          reject();
        }

        const canvas = this.cropper.getCroppedCanvas(EXPORT_OPTIONS);

        canvas.toBlob(
          (blob) => {
            this.$emit('submission', blob);
            return resolve(blob);
          },
          SUBMISSION_IMAGE_TYPE
        );
      });
    },

    /**
     * Debug logs for cropper mixin.
     *
     * @param {...*} logContent whatever you want to log
     */
    log: devLog.bind(this, 'Cropper Mixin:', true),
  },
};
